import { OutstandingAmount } from '@/components/features/shared/OutstandingAmount'
import { Badge } from '@/components/ui/badge'
import { IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CremationData, formatAgreementStatus, getAgreementStatusColor, SubscriptionData } from '@/domain/agreements/agreement'
import { UserRole } from '@/domain/auth/auth'
import { ServiceType } from '@/domain/offerings/offering'
import { formatParcelState, getParcelStateColor, Parcel, ParcelState } from '@/domain/parcels/parcel'
import { useClientBalance, useClientCremations, useClientParcels, useClientSubscriptions } from '@/hooks/api/useClients'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { ArrowRight, Calendar, FileText, MapPin, ShieldCheck, Trophy } from 'lucide-react'
import { useEffect, useState } from 'react'

export const ClientServices = ({ clientId }: { clientId: string }) => {
  const navigate = useNavigate()
  const { hasAnyRole } = useAuthStore()
  const [activeTab, setActiveTab] = useState<string>(ServiceType.ABONOS)
  const [hasSetDefaultTab, setHasSetDefaultTab] = useState(false)

  const canEdit = hasAnyRole([UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin])

  const { data: subscriptionsData, isLoading: subsLoading } = useClientSubscriptions(clientId)
  const { data: cremationsData, isLoading: cremationsLoading } = useClientCremations(clientId)
  const { data: paginatedParcels, isLoading: parcelsLoading } = useClientParcels(clientId)
  const { data: totalBalance, isLoading: isLoadingBalance } = useClientBalance(clientId)
  const parcels = paginatedParcels?.data || []

  const subscriptions = subscriptionsData?.data
  const cremations = cremationsData?.data

  // console.log(subscriptionsData, cremationsData, paginatedParcels)

  useEffect(() => {
    if (!hasSetDefaultTab && !subsLoading && !cremationsLoading && !parcelsLoading) {
      if (subscriptions && subscriptions.length > 0) {
        setActiveTab(ServiceType.ABONOS)
      } else if (cremations && cremations.length > 0) {
        setActiveTab(ServiceType.CREMACIONES)
      } else if (parcels && parcels.length > 0) {
        setActiveTab(ServiceType.PARCELAS)
      }
      setHasSetDefaultTab(true)
    }
  }, [hasSetDefaultTab, subsLoading, cremationsLoading, parcelsLoading, subscriptions, cremations, parcels])

  const goToContract = (id: string | undefined) => {
    if (!id) return;
    if (!canEdit) return;
    navigate({ to: `${Paths.Management.Contracts}/${id}` })
  }

  const goToParcelContract = (contractId?: string, parcelId?: string) => {
    if (!parcelId || !contractId) return;
    if (canEdit) return;
    navigate({ to: `${Paths.Management.ParcelContract.replace('$contractId', contractId).replace('$parcelId', parcelId)}` })
  }

  const renderActions = (id?: string, parcelId?: string) => {
    if (!id) return null;
    if (canEdit) return (
      <div className="flex space-x-2">
        <IconButton
          variant="default"
          title='Ver contratacion'
          onClick={() => goToContract(id)}
        >
          <ArrowRight className="h-3.5 w-3.5" />
        </IconButton>
      </div>
    )
    else if (!canEdit && parcelId) return (
      <div className="flex space-x-2">
        <IconButton
          variant="default"
          title='Ver contratacion'
          onClick={() => goToParcelContract(id, parcelId)}
        >
          <ArrowRight className="h-3.5 w-3.5" />
        </IconButton>
      </div>
    )
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
      <div className="flex items-center justify-between">
        <TabsList>
          <TabsTrigger value={ServiceType.ABONOS}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Abonos
            {subscriptions && subscriptions.length > 0 && (
              <span className="ml-2 text-xs font-semibold text-info px-2 py-1 bg-gray-200 rounded-full">
                {subscriptions.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value={ServiceType.CREMACIONES}>
            <Trophy className="mr-2 h-4 w-4" />
            Cremaciones
            {cremations && cremations.length > 0 && (
              <span className="ml-2 text-xs font-semibold text-info px-2 py-1 bg-gray-200 rounded-full">
                {cremations.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value={ServiceType.PARCELAS}>
            <MapPin className="mr-2 h-4 w-4" />
            Parcelas
            {parcels && parcels.length > 0 && (
              <span className="ml-2 text-xs font-semibold text-info px-2 py-1 bg-gray-200 rounded-full">
                {parcels.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {totalBalance !== undefined && (
          <div className="text-sm flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border/50 bg-muted/30 w-fit">
            <span className="text-muted-foreground">Saldo Total Cliente:</span>
            <OutstandingAmount balance={totalBalance} available={true} />
          </div>
        )}
        {isLoadingBalance && <Skeleton className="h-9 w-32 rounded-full" />}
      </div>

      <TabsContent value={ServiceType.ABONOS}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className='flex flex-col gap-2'>
                <CardTitle>Abonos</CardTitle>
                <CardDescription>Abonos mensuales contratados</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {subsLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : subscriptions && subscriptions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº Contrato</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Deuda</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub: SubscriptionData) => (
                    <TableRow key={sub.id} className="cursor-pointer hover:bg-accent"
                      onClick={() => goToContract(sub.id)}>
                      <TableCell>
                        <span className="font-mono font-medium text-gray-900">
                          {sub.contractNumber}
                        </span>
                      </TableCell>
                      <TableCell>{sub.offeringName}</TableCell>
                      <TableCell>
                        <Badge className={getAgreementStatusColor(sub.status)}>
                          {formatAgreementStatus(sub.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <OutstandingAmount balance={sub.totalOutstandingAmount} available={true} />
                      </TableCell>
                      <TableCell>
                        {renderActions(sub.id)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="mx-auto h-12 w-12 mb-4" />
                <p>No hay abonos registrados</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value={ServiceType.CREMACIONES}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className='flex flex-col gap-2'>
                <CardTitle>Cremaciones</CardTitle>
                <CardDescription>Cremaciones históricas contratadas</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {cremationsLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : cremations && cremations.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº Contrato</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Fecha de contratación</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Deuda</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cremations.map((agreement: CremationData) => (
                    <TableRow key={agreement.id} className="cursor-pointer hover:bg-accent"
                      onClick={() => goToContract(agreement.id)}>
                      <TableCell>
                        <span className="font-mono font-medium text-gray-900">
                          {agreement.contractNumber}
                        </span>
                      </TableCell>
                      <TableCell>{agreement.offeringName}</TableCell>
                      <TableCell>{format(agreement.startDate, 'dd/MM/yyyy')}</TableCell>
                      <TableCell>
                        <Badge className={getAgreementStatusColor(agreement.status)}>
                          {formatAgreementStatus(agreement.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <OutstandingAmount balance={agreement.totalOutstandingAmount} available={true} />
                      </TableCell>
                      <TableCell>
                        {renderActions(agreement.id)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="mx-auto h-12 w-12 mb-4" />
                <p>No hay cremaciones registradas</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value={ServiceType.PARCELAS}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className='flex flex-col gap-2'>
                <CardTitle>Parcelas</CardTitle>
                <CardDescription>Parcelas asociadas a la cuenta titular</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {parcelsLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : parcels && parcels.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº Contrato</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Deuda</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parcels?.map((parcel: Parcel) => (
                    <TableRow key={parcel.id} className="cursor-pointer hover:bg-accent"
                      onClick={() => goToContract(parcel.contractId!)}>
                      <TableCell>
                        <span className="font-mono font-medium text-gray-900">
                          {parcel.contractNumber}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono font-medium text-gray-900 rounded-xl border border-gray-200 px-2 py-1 bg-gray-50">
                          Sección {parcel.section} - Fila {parcel.fila} - Parcela {parcel.parcela}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getParcelStateColor(parcel.state, parcel.parcelContractState)}>
                          {formatParcelState(parcel.state, parcel.parcelContractState)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <OutstandingAmount balance={parcel.outstandingAmount} available={parcel.state === ParcelState.OCCUPIED || parcel.state === ParcelState.SOLD} />
                      </TableCell>
                      <TableCell>
                        {renderActions(parcel.contractId!, parcel.id)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="mx-auto h-12 w-12 mb-4" />
                <p>No hay parcelas registradas</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  )
}
